import React, { useEffect, useState } from "react";
import { useLocation, Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowLeft, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import publicApi from "@/lib/publicApi";
import { parseLocaleFromPath, localePath } from "@/lib/locale";
import { formatEurPlain } from "@/lib/money";

const L = {
  hr: { paid: "Plaćanje uspješno!", paid_body: "Hvala — vaša narudžba je primljena. Javit ćemo Vam se s detaljima o izradi i dostavi.", pending: "Provjera statusa plaćanja…", pending_body: "Ovo može potrajati nekoliko sekundi.", failed: "Plaćanje nije uspjelo", failed_body: "Pokušajte ponovo ili nas kontaktirajte.", continue: "Pregledaj modele", retry: "Pokušaj ponovo", session: "Sesija", amount: "Iznos" },
  en: { paid: "Payment successful!", paid_body: "Thank you — your order has been received. We'll be in touch with build and shipping details.", pending: "Checking payment status…", pending_body: "This may take a few seconds.", failed: "Payment failed", failed_body: "Please try again or contact us.", continue: "Browse models", retry: "Try again", session: "Session", amount: "Amount" },
  de: { paid: "Zahlung erfolgreich!", paid_body: "Danke — Ihre Bestellung ist eingegangen. Wir melden uns mit Bau- und Versanddetails.", pending: "Zahlungsstatus wird geprüft…", pending_body: "Dies kann einige Sekunden dauern.", failed: "Zahlung fehlgeschlagen", failed_body: "Bitte erneut versuchen oder kontaktieren Sie uns.", continue: "Modelle ansehen", retry: "Erneut versuchen", session: "Sitzung", amount: "Betrag" },
  fr: { paid: "Paiement réussi !", paid_body: "Merci — votre commande a été reçue. Nous vous contacterons avec les détails.", pending: "Vérification du paiement…", pending_body: "Cela peut prendre quelques secondes.", failed: "Échec du paiement", failed_body: "Veuillez réessayer ou nous contacter.", continue: "Voir les modèles", retry: "Réessayer", session: "Session", amount: "Montant" },
};

const MAX_POLLS = 6;
const POLL_INTERVAL_MS = 2000;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { locale } = parseLocaleFromPath(useLocation().pathname);
  const t = L[locale] || L.hr;
  const { clear } = useCart();
  const [state, setState] = useState({ status: "pending", payment_status: null, amount: null, error: null });

  useEffect(() => {
    let cancelled = false;
    let attempts = 0;

    async function poll() {
      if (cancelled || !sessionId) return;
      attempts += 1;
      try {
        const { data } = await publicApi.get(`/checkout/session/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", payment_status: "paid", amount: data.amount_eur, error: null });
          clear();
          return;
        }
        if (data.payment_status === "failed" || data.status === "expired") {
          setState({ status: "failed", payment_status: data.payment_status, amount: data.amount_eur, error: null });
          return;
        }
        // still pending
        if (attempts >= MAX_POLLS) {
          setState({ status: "timeout", payment_status: data.payment_status, amount: data.amount_eur, error: null });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        setState({ status: "failed", payment_status: null, amount: null, error: err?.response?.data?.detail || "Could not check payment status." });
      }
    }

    if (sessionId) poll();
    else setState({ status: "failed", payment_status: null, amount: null, error: "No session_id." });

    return () => { cancelled = true; };
  }, [sessionId, clear]);

  return (
    <>
      <SEO title={`${t.paid} — Tambure Žmegač`} description={t.paid_body} />
      <section className="bg-background grain-bg min-h-[60vh] flex items-center">
        <div className="max-w-xl mx-auto px-5 py-16 text-center w-full" data-testid="checkout-success-page">
          {state.status === "paid" && (
            <>
              <CheckCircle2 className="h-16 w-16 text-primary mx-auto mb-5" strokeWidth={1.2} />
              <h1 className="font-serif text-4xl text-foreground" data-testid="success-title">{t.paid}</h1>
              <p className="mt-3 text-base text-foreground/75 max-w-md mx-auto">{t.paid_body}</p>
              <div className="mt-8 rounded-lg border border-border/60 bg-card p-5 inline-block text-left">
                <div className="text-xs text-muted-foreground">{t.amount}</div>
                <div className="font-serif text-2xl text-primary mt-1" data-testid="success-amount">{formatEurPlain(state.amount || 0)}</div>
                <div className="mt-3 text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{t.session}</div>
                <code className="text-xs text-foreground/70 break-all" data-testid="success-session-id">{sessionId}</code>
              </div>
              <div className="mt-8">
                <Link to={localePath(locale, "/models")}>
                  <Button className="shadow-interactive">{t.continue} <ArrowRight className="h-4 w-4 ml-1" strokeWidth={1.5} /></Button>
                </Link>
              </div>
            </>
          )}
          {(state.status === "pending" || state.status === "timeout") && (
            <>
              <Loader2 className="h-12 w-12 text-primary mx-auto mb-5 animate-spin" strokeWidth={1.2} />
              <h1 className="font-serif text-3xl text-foreground">{t.pending}</h1>
              <p className="mt-2 text-sm text-foreground/70">{t.pending_body}</p>
            </>
          )}
          {state.status === "failed" && (
            <>
              <XCircle className="h-16 w-16 text-destructive mx-auto mb-5" strokeWidth={1.2} />
              <h1 className="font-serif text-3xl text-foreground">{t.failed}</h1>
              <p className="mt-2 text-sm text-foreground/70">{t.failed_body}</p>
              {state.error && <p className="mt-2 text-xs text-destructive">{state.error}</p>}
              <div className="mt-8">
                <Link to={localePath(locale, "/cart")}>
                  <Button variant="outline">{t.retry}</Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}
